import { Component, computed, effect, inject, input, signal } from '@angular/core';
import { Router } from '@angular/router';
import { TranslatePipe } from '@ngx-translate/core';
import { ButtonModule } from 'primeng/button';
import { SkeletonModule } from 'primeng/skeleton';
import { AuthService } from '../../../../services/auth.service';
import {
    FunpayHelper,
    isFunpayLive,
    IFunpayExistingAccount,
    IFunpayLinkExistingData,
    IFunpayOnboardingInitialData,
    IFunpayOnboardingStatus
} from '../../../../services/funpay.service';
import { FunPayLinkExistingComponent } from './link-existing/link-existing';
import { FunPayOnboardingFormComponent } from './onboarding-form/onboarding-form';
import { FunPayOnboardingStatusComponent } from './onboarding-status/onboarding-status';

@Component({
    selector: 'funpay-onboarding',
    templateUrl: './funpay-onboarding.html',
    imports: [FunPayOnboardingFormComponent, FunPayOnboardingStatusComponent, FunPayLinkExistingComponent, SkeletonModule, ButtonModule, TranslatePipe]
})
export class FunPayOnboardingComponent {
    id = input.required<string>();

    private onboarding = inject(FunpayHelper);
    private auth = inject(AuthService);
    private router = inject(Router);

    isSuperadmin = computed(() => this.auth.currentUser()?.role === 'superadmin');

    loading = signal<boolean>(true);
    loadError = signal<string | null>(null);
    status = signal<IFunpayOnboardingStatus | null>(null);

    busy = signal<boolean>(false);
    actionError = signal<string | null>(null);

    editing = signal<boolean>(false);

    /** Approved accounts elsewhere in the organisation this venue could share instead of applying. */
    existingAccounts = signal<IFunpayExistingAccount[]>([]);
    /** Which path the user picked before an application exists. */
    path = signal<'choose' | 'new' | 'existing'>('choose');

    hasStarted = computed(() => {
        const current = this.status();
        return !!current?.state || !!current?.ids?.legalEntityId;
    });

    showForm = computed(() => !this.hasStarted() || this.editing());
    showChoice = computed(() => !this.hasStarted() && this.existingAccounts().length > 0 && this.path() === 'choose');
    showLinkExisting = computed(() => !this.hasStarted() && this.existingAccounts().length > 0 && this.path() === 'existing');

    constructor() {
        effect(() => {
            const clientId = this.id();
            if (clientId) {
                void this.load(clientId);
            }
        });
    }

    async load(clientId: string): Promise<void> {
        this.loading.set(true);
        this.loadError.set(null);

        try {
            const [result, existing] = await Promise.all([this.onboarding.getStatus(clientId), this.onboarding.getExistingAccounts(clientId)]);
            // Not being able to list reusable accounts only hides that option; a new application still works.
            this.existingAccounts.set(existing.accounts ?? []);

            if (result.error || !result.status) {
                this.loadError.set(result.error ?? 'ONBOARDING_STATUS_FAILED');
                return;
            }

            // Approved and ready, so the application has nothing left to show.
            if (isFunpayLive(result.status.ids?.verification)) {
                await this.router.navigate(['/organizations', this.auth.organizationId(), 'funpay', clientId], { replaceUrl: true });
                return;
            }

            this.status.set(result.status);
        } finally {
            this.loading.set(false);
        }
    }

    /** First submit, and the retry for an application that stopped part way. */
    async startOnboarding(onboardingData?: IFunpayOnboardingInitialData): Promise<void> {
        const clientId = this.id();
        if (!clientId || this.busy()) {
            return;
        }

        this.busy.set(true);
        this.actionError.set(null);

        try {
            const result = await this.onboarding.initOnboarding(clientId, onboardingData);

            if (result.error || !result.onboardingLink) {
                this.actionError.set(result.error ?? 'ONBOARDING_INIT_FAILED');
                await this.load(clientId);
                return;
            }

            this.handOverToAdyen(result.onboardingLink);
        } finally {
            this.busy.set(false);
        }
    }

    /** Share an approved account from another venue: no KYC, only a new balance account and store. */
    async linkExisting(data: IFunpayLinkExistingData): Promise<void> {
        const clientId = this.id();
        if (!clientId || this.busy()) {
            return;
        }

        this.busy.set(true);
        this.actionError.set(null);

        try {
            const result = await this.onboarding.linkExisting(clientId, data);
            if (result.error) {
                this.actionError.set(result.error);
                return;
            }
            this.path.set('choose');
            await this.load(clientId);
        } finally {
            this.busy.set(false);
        }
    }

    /** Reopen hosted onboarding for an account that already exists. */
    async continueOnboarding(): Promise<void> {
        const clientId = this.id();
        if (!clientId || this.busy()) {
            return;
        }

        this.busy.set(true);
        this.actionError.set(null);

        try {
            const result = await this.onboarding.createOnboardingLink(clientId);

            if (result.error || !result.onboardingLink) {
                this.actionError.set(result.error ?? 'FAILED_TO_CREATE_ONBOARDING_LINK');
                return;
            }

            this.handOverToAdyen(result.onboardingLink);
        } finally {
            this.busy.set(false);
        }
    }

    /** Superadmin repair: push the stored account holder into the venue settings again. */
    async resyncAccountHolder(): Promise<void> {
        const clientId = this.id();
        if (!clientId || this.busy() || !this.isSuperadmin()) {
            return;
        }

        this.busy.set(true);
        this.actionError.set(null);

        try {
            const result = await this.onboarding.syncAccountHolder(clientId);

            if (result.error) {
                this.actionError.set(result.error);
                return;
            }

            await this.load(clientId);
        } finally {
            this.busy.set(false);
        }
    }

    /** Adyen's hosted onboarding link is short lived, so use it immediately. */
    private handOverToAdyen(url: string): void {
        window.location.href = url;
    }
}
